import React, { useMemo, useState, useEffect } from 'react';

/**
 * Mottagarväljare vid publicering av mötesprotokoll.
 * - Mötesdeltagare förkryssas (kan avmarkeras).
 * - Övriga projektmedlemmar listas okryssade och kan markeras.
 * De valda får både SE protokollet och få notis/mail.
 */

const ROLE_LABELS = {
  customer: 'Kund', kund: 'Kund',
  consultant: 'Byggkonsult', byggkonsult: 'Byggkonsult',
  architect: 'Inredningsarkitekt', inredningsarkitekt: 'Inredningsarkitekt',
  builder: 'Byggare', byggare: 'Byggare',
  subcontractor: 'UE', UE: 'UE',
  projector: 'Projektör', projektor: 'Projektör',
  material_supplier: 'Materialleverantör', materialleverantör: 'Materialleverantör',
  external: 'Extern'
};
const roleLabel = (r) => ROLE_LABELS[r] || r || '';
// userId kan vara en sträng, ett ObjectId eller ett populerat objekt {_id,...} —
// normalisera alltid till en sträng-id.
const idOf = (u) => {
  if (!u) return null;
  if (typeof u === 'object') return String(u._id || u.id || '') || null;
  return String(u);
};
const keyOf = (p) => (p.userId ? `u:${p.userId}` : `e:${(p.email || '').toLowerCase()}`);

const RecipientRow = ({ person, checked, onToggle }) => (
  <label className="flex items-center justify-between py-1.5 px-1 cursor-pointer hover:bg-gray-50 rounded">
    <div className="flex items-center min-w-0">
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        className="rounded border-gray-300 text-leadme-pink focus:ring-leadme-pink flex-shrink-0"
      />
      <span className="ml-2 text-sm text-gray-700 truncate">
        {person.name}
        <span className="text-gray-400"> · {roleLabel(person.role)}</span>
      </span>
    </div>
  </label>
);

const ProtocolRecipientModal = ({
  isOpen,
  onClose,
  onConfirm,
  attendees = [],
  projectMembers = [],
  saving = false,
  protocolTitle = ''
}) => {
  // Mötesdeltagare (förvalda)
  const attendeeRows = useMemo(() => {
    const rows = (attendees || [])
      .filter(a => a && (a.userId || a.email))
      .map(a => ({
        userId: idOf(a.userId),
        email: a.email || null,
        name: a.name || roleLabel(a.role) || 'Deltagare',
        role: a.role || ''
      }))
      .filter(a => a.userId || a.email);
    // dedup på nyckel (samma person kan råka stå dubbelt)
    return rows.filter((a, i, arr) => arr.findIndex(x => keyOf(x) === keyOf(a)) === i);
  }, [attendees]);

  const attendeeKeys = useMemo(() => new Set(attendeeRows.map(keyOf)), [attendeeRows]);

  // Övriga projektmedlemmar (ej redan deltagare), deduppade
  const otherRows = useMemo(() => {
    const rows = (projectMembers || [])
      .filter(m => m && m.userId)
      .map(m => ({
        userId: idOf(m.userId),
        email: m.email || null,
        name: m.name || roleLabel(m.role),
        role: m.role || ''
      }))
      .filter(m => m.userId && !attendeeKeys.has(keyOf(m)));
    // dedup inom övriga
    return rows.filter((m, i, arr) => arr.findIndex(x => keyOf(x) === keyOf(m)) === i);
  }, [projectMembers, attendeeKeys]);

  const [selected, setSelected] = useState({});

  // (Åter)initiera urvalet varje gång modalen öppnas
  useEffect(() => {
    if (!isOpen) return;
    const s = {};
    attendeeRows.forEach(a => { s[keyOf(a)] = true; });
    otherRows.forEach(o => { s[keyOf(o)] = false; });
    setSelected(s);
    // attendeeRows och otherRows är UTELÄMNADE MED AVSIKT. Urvalet ska sättas
    // när modalen öppnas och sedan ägas av användarens klick.
    //
    // Att ta med dem skulle inte bara vara onödigt, det skulle gå sönder:
    // ProtocolBuilder skickar attendees={meeting?.attendees || []}, så för ett
    // möte utan deltagare är propen en NY array vid varje render. Då blir
    // attendeeRows ny varje render, effekten kör om, och setSelected nollställer
    // bockarna — övriga projektmedlemmar går inte att markera.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const toggle = (k) => setSelected(prev => ({ ...prev, [k]: !prev[k] }));
  const allOthersSelected = otherRows.length > 0 && otherRows.every(o => selected[keyOf(o)]);
  const toggleAllOthers = (checked) => setSelected(prev => {
    const next = { ...prev };
    otherRows.forEach(o => { next[keyOf(o)] = checked; });
    return next;
  });

  const chosen = [...attendeeRows, ...otherRows].filter(p => selected[keyOf(p)]);

  const handleConfirm = () => {
    onConfirm(chosen.map(p => ({
      userId: p.userId || undefined,
      email: p.email || undefined,
      name: p.name,
      role: p.role
    })));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center p-0 sm:p-4 z-[9999]">
      <div className="bg-white rounded-t-2xl sm:rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-5 sm:p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Publicera protokoll</h3>
          <p className="text-sm text-gray-500 mb-4">
            Välj vilka som ska se protokollet{protocolTitle ? ` "${protocolTitle}"` : ''} och få notis/mail.
          </p>

          {/* Mötesdeltagare */}
          <div className="mb-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Mötesdeltagare</p>
            {attendeeRows.length === 0 ? (
              <p className="text-sm text-gray-400 py-1">Inga registrerade deltagare</p>
            ) : (
              <div className="divide-y divide-gray-100">
                {attendeeRows.map(a => (
                  <RecipientRow key={keyOf(a)} person={a} checked={!!selected[keyOf(a)]} onToggle={() => toggle(keyOf(a))} />
                ))}
              </div>
            )}
          </div>

          {/* Övriga projektmedlemmar */}
          {otherRows.length > 0 && (
            <div className="mb-2">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Övriga projektmedlemmar</p>
                <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={allOthersSelected}
                    onChange={(e) => toggleAllOthers(e.target.checked)}
                    className="rounded border-gray-300 text-leadme-pink focus:ring-leadme-pink"
                  />
                  Välj alla
                </label>
              </div>
              <div className="divide-y divide-gray-100">
                {otherRows.map(o => (
                  <RecipientRow key={keyOf(o)} person={o} checked={!!selected[keyOf(o)]} onToggle={() => toggle(keyOf(o))} />
                ))}
              </div>
            </div>
          )}

          <div className="mt-5 flex flex-col-reverse sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-3">
            <span className="text-xs text-gray-500">{chosen.length} mottagare valda</span>
            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
              <button
                onClick={onClose}
                disabled={saving}
                className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Avbryt
              </button>
              <button
                onClick={handleConfirm}
                disabled={saving || chosen.length === 0}
                className="px-3 py-2 text-sm font-medium text-white bg-leadme-pink rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Publicerar...
                  </>
                ) : 'Publicera'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProtocolRecipientModal;
